import { loadAuthorizedToViewEvent } from "@shared/utils/password/utils";
import { useEffect, useRef, useState } from "react";
import { EventAuthorizationProps } from "./EventAuthorization.props";

/**
 * @deprecated See GlobalAuthorizationProvider
 */
export function useEventAuthorization(props: EventAuthorizationProps) {
  const {
    isLoading,
    eventData,
    ticketsAuthorizedByPassword,
    updateAuthorizedTicketArray,
  } = props;

  const hasData = !isLoading && Boolean(eventData);
  const isPasswordProtected =
    eventData &&
    "is_password_protected" in eventData &&
    Boolean(eventData.is_password_protected);
  const canSkipAuthCheck = hasData && !isPasswordProtected;

  const [checkingAuth, setCheckingAuth] = useState(() => !canSkipAuthCheck);
  const [eventPasswordAuthorized, setEventPasswordAuthorized] = useState(
    () => canSkipAuthCheck
  );

  // Reset auth state when the event changes (client-side navigation).
  // Without this, useState values from the previous event persist because
  // Next.js reuses the same page component instance for same-route navigations.
  const currentEventId = eventData?.id;
  const prevEventIdRef = useRef(currentEventId);

  useEffect(() => {
    if (prevEventIdRef.current !== currentEventId) {
      prevEventIdRef.current = currentEventId;

      const newCanSkip =
        !isLoading && Boolean(eventData) && !isPasswordProtected;
      setCheckingAuth(!newCanSkip);
      setEventPasswordAuthorized(newCanSkip);
    }
  }, [currentEventId, isLoading, eventData, isPasswordProtected]);

  useEffect(() => {
    if (eventData && ticketsAuthorizedByPassword === null) {
      loadAuthorizedToViewEvent(eventData).then(
        ({ isAuthorized, ticketTypesAuthorized: ticketsToDisplay }) => {
          setEventPasswordAuthorized(isAuthorized);
          if (ticketsToDisplay && ticketsToDisplay !== null) {
            updateAuthorizedTicketArray(ticketsToDisplay);
          }
          setCheckingAuth(false);
        }
      );
    }
  }, [eventData, ticketsAuthorizedByPassword, updateAuthorizedTicketArray]);

  const onAuthorize = (ticketIds: Array<number>) => {
    setEventPasswordAuthorized(true);
    if (ticketIds) {
      updateAuthorizedTicketArray(ticketIds);
    }
  };

  // Only show the modal once the check is done AND user is NOT authorized.
  // While checking, nothing extra shows -- the page content renders normally.
  const showPasswordModal =
    !checkingAuth && !eventPasswordAuthorized && Boolean(eventData);

  // Show a spinner while we're still checking auth for password-protected events.
  // Non-password events skip the check entirely so this stays false for them.
  const showPageSpinner = checkingAuth && Boolean(eventData);

  return {
    eventPasswordAuthorized,
    showPasswordModal,
    showPageSpinner,
    onAuthorize,
  };
}
